import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
  Button,
  Breadcrumb,
  BreadcrumbItem,
} from 'reactstrap';
import classnames from 'classnames';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import BreadCrumb from '../../../../Components/Common/BreadCrumb';
import ConfirmModal from '../../../../Components/crude/ConfirmModal';
import * as api from './tenderOpeningApi';
import {
  TO_STATUS,
  TO_STATUS_BADGE,
  isTabEditable,
  isClauseEditable,
} from './tenderOpeningConstants';

import StatusDot from './components/StatusDot';
import SendRequestHpsTab from './components/SendRequestHpsTab';
import SendInvitationDmutTab from './components/SendInvitationDmutTab';
import SendPanitiaTab from './components/SendPanitiaTab';

// Crude Tender Opening — Detail/Edit page (live API), one component driven by
// `mode`:
//   mode='view' (/dtl/:id) — every tab renders read-only, exactly as before.
//   mode='edit' (/edit/:id) — Send/Recall/Resubmit actions are wired to the
//     live per-step endpoints (contract sections 3-10), gated by
//     isTabEditable/isClauseEditable per rule §Detail.1.
//
// Loads GET /v1/TenderOpening/{id} (tenderOpeningApi.getTenderOpening) — one
// combined endpoint for the info card (Procurement No / Type / Method /
// Create Date) plus the six tabs. Every write action re-fetches this endpoint
// afterward so status/checklist/badges stay in sync with the server.
//
// Tabs 3-6 (the Send tabs) show a small round status dot next to their title,
// green/grey per that record's checklist — matching the list's checklist cells.
// Requirement and Supporting Document have no checklist step, so no dot.

// Tab config. `check` is the checklist key that drives the round status dot and
// the tab's own editability (null = no checklist step / always view-only,
// except Requirement's Clause Detail section — see isClauseEditable).
// 3 tab mengikuti desain acuan. Tab Requirement/Supporting Document/Freight
// yang lama dihapus: info requirement & dokumen kini tampil sebagai kartu di
// dalam tab-tab ini, edit clause (soft recall) pindah ke tab DMUT, dan Send
// Freight pindah ke tab HPS ("HPS Market & Freight Cost").
const TABS = [
  {
    id: 1,
    icon: 'ri-money-dollar-circle-line',
    label: 'SENDING REQUEST HPS',
    check: 'requestHps',
  },
  {
    id: 2,
    icon: 'ri-mail-send-line',
    label: 'SENDING INVITATION TO DMUT',
    check: 'inviteDmutDocument',
  },
  {
    id: 3,
    icon: 'ri-team-line',
    label: 'SENDING INVITATION TO PANITIA TENDER',
    check: 'invitePanitiaTender',
  },
];

const RECALL_COPY = {
  soft: {
    title: 'Soft Recall — Invitation DMUT',
    message:
      'This pulls back the DMUT invitation and reopens Clause Detail for editing. Continue?',
    confirmLabel: 'Soft Recall',
  },
  hard: {
    title: 'Hard Recall — Invitation Panitia Lelang',
    message:
      'This resets Planning, Procurement, and Tender Opening for this record back to Draft/On Hard Recall. Continue?',
    confirmLabel: 'Hard Recall',
  },
};

// Confirmation copy for the non-comment Send/Resubmit actions — each opens a
// plain ConfirmModal (no notes) before its API call runs. Save as Draft is
// intentionally absent (it runs directly, no confirmation).
const ACTION_CONFIRM = {
  hps: {
    title: 'Send Request HPS',
    message:
      'This sends the Request HPS, generates the HPS number, and marks the step complete. Continue?',
    confirmLabel: 'Send Request HPS',
    confirmColor: 'success',
  },
  dmut: {
    title: 'Send Invitation DMUT',
    message:
      'This emails the selected DMUT recipients and marks the step complete. Continue?',
    confirmLabel: 'Send Invitation DMUT',
    confirmColor: 'success',
  },
  freight: {
    title: 'Send Request Freightcost',
    message:
      'This sends the Freightcost request, generates the request number, and marks the step complete. Continue?',
    confirmLabel: 'Send Request Freightcost',
    confirmColor: 'success',
  },
  resubmit: {
    title: 'Resubmit Clause',
    message:
      'This resubmits the updated clauses and exits On Soft Recall (DMUT must be invited again). Continue?',
    confirmLabel: 'Resubmit Clause',
    confirmColor: 'primary',
  },
};

// Loading skeleton — mirrors the real page layout (header, 4 info cards, tab
// strip, tab content) with Bootstrap `placeholder-glow` bars so the transition
// into the loaded content is seamless (no jump from bare text to full layout).
// Wrapper markup is copied from the main render below so spacing matches.
const DetailSkeleton = ({ isEdit }) => (
  <React.Fragment>
    <div className='page-content'>
      <Container fluid>
        <BreadCrumb
          title={isEdit ? 'Tender Opening Edit' : 'Tender Opening Detail'}
          pageTitle='CRUDE'
        />

        <div className='email-wrapper d-lg-flex gap-1 mx-n4 p-1'>
          <div className='email-content minimal-border'>
            <div className='p-4 pb-0 placeholder-glow'>
              {/* ---- Header: title + status badges ---- */}
              <div className='d-flex align-items-center flex-wrap gap-3 mb-3 pb-3 border-bottom'>
                <span
                  className='placeholder rounded-circle flex-shrink-0'
                  style={{ width: 38, height: 38 }}
                ></span>
                <span className='placeholder col-4 flex-grow-1'></span>
                <div className='d-flex align-items-center flex-wrap gap-2'>
                  <span
                    className='placeholder rounded-pill'
                    style={{ width: 80 }}
                  ></span>
                  <span
                    className='placeholder rounded-pill'
                    style={{ width: 90 }}
                  ></span>
                </div>
              </div>

              {/* ---- Info cards ---- */}
              <Row className='mt-0 mb-2 g-3 d-flex align-items-stretch'>
                {[0, 1, 2, 3].map(i => (
                  <Col xl={3} md={6} className='mb-0' key={i}>
                    <div className='card bg-soft-primary border shadow-none h-100 mb-0'>
                      <div className='card-body py-3'>
                        <div className='d-flex'>
                          <div className='flex-shrink-0 me-3'>
                            <span
                              className='placeholder'
                              style={{ width: 32, height: 32 }}
                            ></span>
                          </div>
                          <div className='flex-grow-1'>
                            <span className='placeholder col-6 d-block mb-2'></span>
                            <span className='placeholder col-9 d-block'></span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>

              {/* ---- Tab strip ---- */}
              <Row className='justify-content-center mb-4'>
                <Col xxl={12}>
                  <CardBody className='checkout-tab'>
                    <div className='d-flex flex-wrap gap-3 mt-1 mb-3 pb-2 border-bottom'>
                      {[0, 1, 2, 3, 4, 5].map(i => (
                        <span
                          key={i}
                          className='placeholder'
                          style={{ width: 120 }}
                        ></span>
                      ))}
                    </div>

                    {/* ---- Tab content ---- */}
                    <span className='placeholder col-12 d-block mb-2'></span>
                    <span className='placeholder col-10 d-block mb-2'></span>
                    <span className='placeholder col-8 d-block mb-2'></span>
                    <span className='placeholder col-11 d-block mb-2'></span>
                    <span className='placeholder col-6 d-block mb-2'></span>
                    <span className='placeholder col-9 d-block'></span>
                  </CardBody>
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </Container>
    </div>
  </React.Fragment>
);

const TenderOpeningDetailView = ({ mode = 'view' }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = mode === 'edit';

  const [record, setRecord] = useState(null);
  const [detail, setDetail] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState(1);

  // Per-action submitting flags — disable the relevant button only, not the
  // whole page, while a write request is in flight.
  const [savingHpsDraft, setSavingHpsDraft] = useState(false);
  const [savingPanitiaDraft, setSavingPanitiaDraft] = useState(false);
  const [sendingHps, setSendingHps] = useState(false);
  const [sendingDmut, setSendingDmut] = useState(false);
  const [sendingPanitia, setSendingPanitia] = useState(false);
  const [sendingFreight, setSendingFreight] = useState(false);
  const [resubmitting, setResubmitting] = useState(false);

  // Soft/Hard Recall share one ConfirmModal instance — `recallModal` is
  // 'soft' | 'hard' | null.
  const [recallModal, setRecallModal] = useState(null);
  const [recallSubmitting, setRecallSubmitting] = useState(false);

  // Non-comment Send/Resubmit actions share one plain ConfirmModal — the tab
  // validates its form first, then calls onSend/onResubmitClauses (as before),
  // which now just stashes { type, payload } here to open the confirm dialog;
  // runConfirmedAction fires the real API call on Confirm.
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionSubmitting, setActionSubmitting] = useState(false);

  // DMUT recipients (Q6) — not in the Tender Opening contract itself; BE
  // confirmed the same list lives on Planning, read via the dedicated
  // GET /v1/Planning/{planningId}/DmutInvitation endpoint (api.getDmutRecipients)
  // keyed on `planningId`, already present on this record. Lazy-loaded the first
  // time the DMUT tab is opened, not on initial page load.
  const [dmutRecipients, setDmutRecipients] = useState(null);
  const [loadingRecipients, setLoadingRecipients] = useState(false);

  const loadRecord = useCallback(async () => {
    try {
      const { record: rec, detail: det } = await api.getTenderOpening(id);
      setRecord(rec);
      setDetail(det);
      setNotFound(false);
    } catch (error) {
      // crude_api_helper rejects with a string message; 404 -> 'Data tidak
      // ditemukan' (contract section 2). Show the not-found panel for that,
      // toast anything else.
      if (String(error).includes('tidak ditemukan')) {
        setNotFound(true);
        setRecord(null);
        setDetail(null);
      } else {
        toast.error(`Failed to load: ${error}`);
      }
    }
  }, [id]);

  useEffect(() => {
    loadRecord();
  }, [loadRecord]);

  // Lazy-load DMUT recipients the first time the DMUT tab (id 4) or the
  // Panitia tab (id 5, which also lists the invited DMUT per the reference
  // design) is opened — in both view and edit mode, so the "no recipients"
  // empty state always reflects real data instead of "not fetched yet".
  // `dmutRecipients !== null` guards against re-fetching on every tab switch.
  useEffect(() => {
    if ((activeTab !== 2 && activeTab !== 3) || dmutRecipients !== null) return;
    if (!record?.planningId) return;
    let active = true;
    setLoadingRecipients(true);
    api
      .getDmutRecipients(record.planningId)
      .then(rows => {
        if (active) setDmutRecipients(rows || []);
      })
      .catch(error => {
        if (active) {
          toast.error(`Failed to load DMUT recipients: ${error}`);
          setDmutRecipients([]);
        }
      })
      .finally(() => {
        if (active) setLoadingRecipients(false);
      });
    return () => {
      active = false;
    };
  }, [activeTab, record?.planningId, dmutRecipients]);

  document.title = record
    ? `Tender Opening ${isEdit ? 'Edit' : '-'} ${record.procId}`
    : `Tender Opening ${isEdit ? 'Edit' : 'Detail'}`;

  const checks = record?.checks || {};
  const toStatus = record?.toStatus;

  // Gating per rule §Detail.1 — see tenderOpeningConstants.js for the full
  // status table. Requirement/Supporting Document have no checklist step, so
  // they are never in this map (Clause Detail is handled separately below).
  const tabEditable = {
    requestHps: isTabEditable(mode, toStatus, 'requestHps', checks),
    inviteDmutDocument: isTabEditable(
      mode,
      toStatus,
      'inviteDmutDocument',
      checks
    ),
    invitePanitiaTender: isTabEditable(
      mode,
      toStatus,
      'invitePanitiaTender',
      checks
    ),
    reqFreightAvailability: isTabEditable(
      mode,
      toStatus,
      'reqFreightAvailability',
      checks
    ),
  };
  const clauseEditable = isClauseEditable(mode, toStatus);

  // Recall buttons have no BE status guard (contract endpoints 6 & 7 — "bisa
  // diklik kapan pun di tab ini"), but showing them once already terminal
  // (Done/On Hard Recall) or, for Soft Recall, once already On Soft Recall
  // (nothing new to recall yet) is a dead end — disabled as a FE-only guard.
  const canSoftRecall =
    isEdit &&
    toStatus !== TO_STATUS.DONE &&
    toStatus !== TO_STATUS.HARD_RECALL &&
    toStatus !== TO_STATUS.SOFT_RECALL;
  const canHardRecall =
    isEdit && toStatus !== TO_STATUS.DONE && toStatus !== TO_STATUS.HARD_RECALL;

  const handleSaveHpsDraft = async payload => {
    setSavingHpsDraft(true);
    try {
      await api.saveHpsDraft(id, payload);
      toast.success('Draft saved.');
      await loadRecord();
    } catch (error) {
      toast.error(`Failed to save draft: ${error}`);
    } finally {
      setSavingHpsDraft(false);
    }
  };

  const handleSavePanitiaDraft = async payload => {
    setSavingPanitiaDraft(true);
    try {
      await api.saveInvitePanitiaDraft(id, payload);
      toast.success('Draft saved.');
      await loadRecord();
    } catch (error) {
      toast.error(`Failed to save draft: ${error}`);
    } finally {
      setSavingPanitiaDraft(false);
    }
  };

  // Send/Resubmit actions open the confirmation dialog first (the tab has
  // already validated its form by the time these are called). The real API
  // call runs in runConfirmedAction after Confirm.
  const handleSendHps = payload => setConfirmAction({ type: 'hps', payload });
  const handleSendDmut = () =>
    setConfirmAction({ type: 'dmut', payload: null });
  // Panitia: konfirmasi kaya (ringkasan paket + dampak proses) dirender oleh
  // SendPanitiaTab sendiri — di sini langsung eksekusi, tanpa ConfirmModal
  // generik (supaya tidak dobel konfirmasi).
  const handleSendPanitia = async payload => {
    setSendingPanitia(true);
    try {
      await api.sendInvitePanitia(id, payload);
      toast.success('Invitation Panitia Lelang sent.');
      await loadRecord();
    } catch (error) {
      toast.error(`Failed to submit action: ${error}`);
    } finally {
      setSendingPanitia(false);
    }
  };
  const handleSendFreight = () =>
    setConfirmAction({ type: 'freight', payload: null });
  const handleResubmitClauses = clauses =>
    setConfirmAction({ type: 'resubmit', payload: clauses });

  // Fired by the confirm dialog. Runs the API call for the pending action,
  // keeping the per-action `sending*`/`resubmitting` flags (so the tab button
  // stays disabled) plus `actionSubmitting` for the modal's own Confirm button.
  const runConfirmedAction = async () => {
    if (!confirmAction) return;
    const { type, payload } = confirmAction;
    setActionSubmitting(true);
    try {
      if (type === 'hps') {
        setSendingHps(true);
        await api.sendHpsRequest(id, payload);
        toast.success('Request HPS sent.');
      } else if (type === 'dmut') {
        setSendingDmut(true);
        await api.sendInviteDmut(id);
        toast.success('Invitation DMUT sent.');
      } else if (type === 'freight') {
        setSendingFreight(true);
        await api.sendFreight(id);
        toast.success('Request Freightcost sent.');
      } else if (type === 'resubmit') {
        setResubmitting(true);
        await api.resubmitClauses(id, payload);
        toast.success('Clauses resubmitted.');
      }
      setConfirmAction(null);
      await loadRecord();
    } catch (error) {
      toast.error(`Failed to submit action: ${error}`);
    } finally {
      setActionSubmitting(false);
      setSendingHps(false);
      setSendingDmut(false);
      setSendingPanitia(false);
      setSendingFreight(false);
      setResubmitting(false);
    }
  };

  // Hard Recall's own record moves to "On Hard Recall" (this record's edit
  // page would otherwise immediately go view-only under the user) and its
  // effects fan out to Planning/ProcurementValidity too — per Q3, redirect
  // back to the List rather than reloading this page. Soft Recall stays on
  // the page and just reloads (only this record changes).
  const confirmRecall = async comment => {
    setRecallSubmitting(true);
    try {
      if (recallModal === 'soft') {
        await api.softRecallDmut(id, comment);
        toast.success('Soft Recall submitted.');
        setRecallModal(null);
        await loadRecord();
      } else if (recallModal === 'hard') {
        await api.hardRecallPanitia(id, comment);
        toast.success('Hard Recall submitted.');
        setRecallModal(null);
        navigate('/crude/tender-opening');
      }
    } catch (error) {
      toast.error(`Failed to submit recall: ${error}`);
    } finally {
      setRecallSubmitting(false);
    }
  };

  const goBackToList = () => navigate('/crude/tender-opening');

  if (notFound) {
    return (
      <div className='page-content'>
        <Container fluid>
          <div className='text-center py-5'>
            <i className='ri-error-warning-line fs-1 text-muted'></i>
            <h5 className='mt-2'>Tender Opening not found</h5>
            <button
              className='btn btn-primary btn-sm mt-2'
              onClick={goBackToList}
            >
              Back to list
            </button>
          </div>
        </Container>
      </div>
    );
  }

  if (!record) {
    return <DetailSkeleton isEdit={isEdit} />;
  }

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <div className='email-wrapper d-lg-flex gap-1 mx-n4 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4 pb-0'>
                {/* Header mengikuti gaya PlanningForm (breadcrumb box +
                    tombol back bundar + judul bold + meta ber-ikon). */}
                <Row className='mt-n2 mb-1 mb-sm-0'>
                  <div className='col-sm order-3 order-sm-2'>
                    <div className='hstack gap-sm-1 align-items-center flex-wrap email-topbar-link'>
                      <h5 className='card-title mb-0 flex-grow-1'>
                        <Breadcrumb listClassName='p-3 py-2 bg-light'>
                          <BreadcrumbItem>
                            <Link to='/dashboard'>Home</Link>
                          </BreadcrumbItem>
                          <BreadcrumbItem>
                            <Link to='/crude/tender-opening'>
                              Tender Opening
                            </Link>
                          </BreadcrumbItem>
                          <BreadcrumbItem active>
                            {isEdit ? 'Edit' : 'Detail'} #{record.procId || '-'}
                          </BreadcrumbItem>
                        </Breadcrumb>
                      </h5>
                    </div>
                  </div>
                </Row>

                <Row className='mb-4'>
                  <Col xs={12}>
                    <Row className='align-items-center g-3'>
                      <Col xs={12} lg>
                        <div className='d-flex align-items-start gap-3'>
                          <Button
                            color='light'
                            className='border shadow-sm rounded-circle d-flex align-items-center justify-content-center p-0'
                            style={{ width: '2.5rem', height: '2.5rem' }}
                            onClick={goBackToList}
                          >
                            <i className='bx bx-arrow-back fs-5 text-dark'></i>
                          </Button>
                          <div className='flex-grow-1'>
                            <h4 className='mb-1 fw-bold text-dark'>
                              {record.procurementType} — {record.title || '-'}
                            </h4>
                            <div className='d-flex flex-wrap align-items-center gap-2 text-muted small'>
                              <i className='bx bx-hash'></i>
                              <span>
                                Procurement ID: {record.procId || '-'}
                              </span>
                              <span className='d-none d-md-inline text-secondary opacity-25'>
                                |
                              </span>
                              <span>{record.procurementMethod}</span>
                              <Badge
                                color={
                                  TO_STATUS_BADGE[record.toStatus] ||
                                  'secondary'
                                }
                                className='fw-semibold px-3 py-1 rounded-pill'
                              >
                                {record.toStatus}
                              </Badge>
                            </div>
                          </div>
                          {/* Detail (dtl) route is always read-only — offer a
                              one-click jump to the editable route while the
                              record is not terminal. */}
                          {!isEdit &&
                            record.toStatus !== TO_STATUS.DONE &&
                            record.toStatus !== TO_STATUS.HARD_RECALL && (
                              <Button
                                color='primary'
                                onClick={() =>
                                  navigate(`/crude/tender-opening/edit/${id}`)
                                }
                              >
                                <i className='ri-pencil-line align-bottom me-1'></i>
                                Edit Tender Opening
                              </Button>
                            )}
                        </div>
                      </Col>
                    </Row>
                    <hr className='mt-3 mb-0 border-secondary opacity-10' />
                  </Col>
                </Row>

                <Row className='justify-content-center mb-4'>
                  <Col xxl={12}>
                    <CardBody className='checkout-tab'>
                      <div className='mx-n4 px-4 message-list-scroll'>
                        <Nav
                          tabs
                          className='nav-tabs-custom nav-primary mt-1 mb-3'
                        >
                          {TABS.map(tab => (
                            <NavItem key={tab.id}>
                              <NavLink
                                className={classnames('cursor-pointer', {
                                  active: activeTab === tab.id,
                                })}
                                onClick={() => setActiveTab(tab.id)}
                              >
                                <i
                                  className={`${tab.icon} align-middle me-1`}
                                ></i>
                                <span className='fw-semibold'>{tab.label}</span>
                                {/* Round status dot on the Send tabs
                                    (rule §Detail.3). */}
                                {tab.check && (
                                  <StatusDot
                                    done={!!checks[tab.check]}
                                    label={tab.label}
                                  />
                                )}
                              </NavLink>
                            </NavItem>
                          ))}
                        </Nav>
                      </div>

                      <TabContent activeTab={activeTab}>
                        <TabPane tabId={1}>
                          {activeTab === 1 && (
                            <SendRequestHpsTab
                              record={record}
                              detail={detail}
                              sent={!!checks.requestHps}
                              editable={tabEditable.requestHps}
                              onSaveDraft={handleSaveHpsDraft}
                              onSend={handleSendHps}
                              savingDraft={savingHpsDraft}
                              sending={sendingHps}
                              freightSent={!!checks.reqFreightAvailability}
                              freightEditable={
                                tabEditable.reqFreightAvailability
                              }
                              onSendFreight={handleSendFreight}
                              sendingFreight={sendingFreight}
                            />
                          )}
                        </TabPane>
                        <TabPane tabId={2}>
                          {activeTab === 2 && (
                            <SendInvitationDmutTab
                              record={record}
                              detail={detail}
                              onBack={goBackToList}
                              sent={!!checks.inviteDmutDocument}
                              editable={tabEditable.inviteDmutDocument}
                              onSend={handleSendDmut}
                              sending={sendingDmut}
                              canSoftRecall={canSoftRecall}
                              onSoftRecall={() => setRecallModal('soft')}
                              recipients={dmutRecipients || []}
                              loadingRecipients={loadingRecipients}
                              clauseEditable={clauseEditable}
                              onResubmitClauses={handleResubmitClauses}
                              resubmitting={resubmitting}
                            />
                          )}
                        </TabPane>
                        <TabPane tabId={3}>
                          {activeTab === 3 && (
                            <SendPanitiaTab
                              record={record}
                              detail={detail}
                              onBack={goBackToList}
                              sent={!!checks.invitePanitiaTender}
                              editable={tabEditable.invitePanitiaTender}
                              onSaveDraft={handleSavePanitiaDraft}
                              savingDraft={savingPanitiaDraft}
                              onSend={handleSendPanitia}
                              sending={sendingPanitia}
                              canHardRecall={canHardRecall}
                              onHardRecall={() => setRecallModal('hard')}
                              recipients={dmutRecipients || []}
                              loadingRecipients={loadingRecipients}
                            />
                          )}
                        </TabPane>
                      </TabContent>
                    </CardBody>
                  </Col>
                </Row>
              </div>
            </div>
          </div>
        </Container>
        <ToastContainer closeButton={false} limit={1} />
      </div>

      <ConfirmModal
        show={recallModal !== null}
        title={recallModal ? RECALL_COPY[recallModal].title : ''}
        message={recallModal ? RECALL_COPY[recallModal].message : ''}
        confirmLabel={recallModal ? RECALL_COPY[recallModal].confirmLabel : ''}
        confirmColor='danger'
        showNotes
        notesLabel='Comment'
        notesPlaceholder='Optional — reason for the recall'
        onConfirm={confirmRecall}
        onClose={() => (recallSubmitting ? null : setRecallModal(null))}
      />

      {/* Plain confirmation (no notes) for the Send/Resubmit actions. */}
      <ConfirmModal
        show={confirmAction !== null}
        title={confirmAction ? ACTION_CONFIRM[confirmAction.type].title : ''}
        message={
          confirmAction ? ACTION_CONFIRM[confirmAction.type].message : ''
        }
        confirmLabel={
          confirmAction ? ACTION_CONFIRM[confirmAction.type].confirmLabel : ''
        }
        confirmColor={
          confirmAction
            ? ACTION_CONFIRM[confirmAction.type].confirmColor
            : 'primary'
        }
        onConfirm={runConfirmedAction}
        onClose={() => (actionSubmitting ? null : setConfirmAction(null))}
      />
    </React.Fragment>
  );
};

export default TenderOpeningDetailView;
